import React from 'react';
import { PaintBrushIcon } from "@heroicons/react/24/outline";

export type ColorByOption = 'none' | 'site' | 'component' | 'subComponent' | 'turbine' | 'outcome' | 'timePeriod';
export type MarkerByOption = 'none' | 'site' | 'component' | 'subComponent' | 'turbine' | 'outcome';
export type SizeByOption = 'uniform' | 'operationTime' | 'recent';

interface PlotCustomizationProps {
  colorBy: ColorByOption;
  markerBy: MarkerByOption;
  sizeBy: SizeByOption;
  showLegend: boolean;
  onColorByChange: (value: ColorByOption) => void;
  onMarkerByChange: (value: MarkerByOption) => void;
  onSizeByChange: (value: SizeByOption) => void;
  onShowLegendChange: (value: boolean) => void;
}

export const PlotCustomization: React.FC<PlotCustomizationProps> = ({
  colorBy,
  markerBy,
  sizeBy,
  showLegend,
  onColorByChange,
  onMarkerByChange,
  onSizeByChange,
  onShowLegendChange,
}) => {
  const colorOptions: { value: ColorByOption; label: string }[] = [
    { value: 'none', label: 'None (Default)' },
    { value: 'site', label: 'Site' },
    { value: 'component', label: 'Component' },
    { value: 'subComponent', label: 'Sub-Component' },
    { value: 'turbine', label: 'Turbine Model' },
    { value: 'outcome', label: 'Outcome Type' },
    { value: 'timePeriod', label: 'Time Period' },
  ];

  const markerOptions: { value: MarkerByOption; label: string }[] = [
    { value: 'none', label: 'None (Circle)' },
    { value: 'site', label: 'Site' },
    { value: 'component', label: 'Component' },
    { value: 'subComponent', label: 'Sub-Component' },
    { value: 'turbine', label: 'Turbine Model' },
    { value: 'outcome', label: 'Outcome Type' },
  ];

  const sizeOptions: { value: SizeByOption; label: string }[] = [
    { value: 'uniform', label: 'Uniform' },
    { value: 'operationTime', label: 'Operation Time' },
    { value: 'recent', label: 'Recent First' },
  ];

  return (
    <div className="space-y-4">
      <div>
          <div className="flex items-center space-x-2">
            <PaintBrushIcon className="w-5 h-5 text-blue-600" />
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Plot Customization</h3>
        </div>
        {/* Color By */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Color By
          </label>
          <div className="relative">
            <select
              value={colorBy}
              onChange={(e) => onColorByChange(e.target.value as ColorByOption)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none pr-8"
            >
              {colorOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Marker By */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Marker By
          </label>
          <div className="relative">
            <select
              value={markerBy}
              onChange={(e) => onMarkerByChange(e.target.value as MarkerByOption)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none pr-8"
            >
              {markerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Size By */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Size By
          </label>
          <div className="relative">
            <select
              value={sizeBy}
              onChange={(e) => onSizeByChange(e.target.value as SizeByOption)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 appearance-none pr-8"
            >
              {sizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Show Legend */}
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-gray-600">
            Show Legend
          </label>
          <button
            onClick={() => onShowLegendChange(!showLegend)}
            className={`
              relative inline-flex h-5 w-9 items-center rounded-full transition-colors
              ${showLegend ? 'bg-blue-600' : 'bg-gray-200'}
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                ${showLegend ? 'translate-x-5' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {/* Reset Button */}
        <button
          onClick={() => {
            onColorByChange('none');
            onMarkerByChange('none');
            onSizeByChange('uniform');
            onShowLegendChange(false);
          }}
          className="w-full mt-3 px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Reset to Default
        </button>
      </div>
    </div>
  );
};

export default PlotCustomization;
